const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;
const DEFAULT_WARNING_MS = 3 * 60 * 1000;

export const IDLE_TIMEOUT_MS = Number(process.env.REACT_APP_IDLE_TIMEOUT_MS) > 0
  ? Number(process.env.REACT_APP_IDLE_TIMEOUT_MS)
  : DEFAULT_TIMEOUT_MS;
export const IDLE_WARNING_MS = Number(process.env.REACT_APP_IDLE_WARNING_MS) >= 0
  ? Number(process.env.REACT_APP_IDLE_WARNING_MS)
  : DEFAULT_WARNING_MS;

export const IDLE_ACTIVITY_STORAGE_KEY = "betterintentions.auth.lastActivityAt";
export const IDLE_TIMEOUT_STORAGE_KEY = "betterintentions.auth.timeoutAt";
export const IDLE_BROADCAST_CHANNEL = "betterintentions-auth-activity";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll", "mousemove"];

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function readStoredNumber(storage, key) {
  try {
    return safeNumber(storage?.getItem?.(key));
  } catch {
    return null;
  }
}

function writeStoredNumber(storage, key, value) {
  try {
    storage?.setItem?.(key, String(value));
  } catch {}
}

function createDefaultBroadcastChannel(name) {
  if (typeof BroadcastChannel === "undefined") return null;
  return new BroadcastChannel(name);
}

export function createIdleSessionController({
  timeoutMs = IDLE_TIMEOUT_MS,
  warningMs = IDLE_WARNING_MS,
  onTimeout,
  onWarningChange,
  requireWarningAcknowledgement = true,
  windowObj = typeof window !== "undefined" ? window : null,
  documentObj = typeof document !== "undefined" ? document : null,
  storage = typeof window !== "undefined" ? window.localStorage : null,
  nowFn = () => Date.now(),
  createBroadcastChannel = createDefaultBroadcastChannel,
} = {}) {
  let timerId = null;
  let warningTickIntervalId = null;
  let stopped = true;
  let timedOut = false;
  let lastActivityAt = nowFn();
  let warningVisible = false;
  let lastWarningSecond = null;
  let broadcastChannel = null;
  let onStorage = null;
  let onVisibility = null;
  let onActivity = null;
  let onBroadcast = null;

  const hasBrowserObjects = Boolean(windowObj && documentObj);
  const normalizedWarningMs = Math.max(0, Math.min(timeoutMs, warningMs));

  function getRemainingMs() {
    return timeoutMs - (nowFn() - lastActivityAt);
  }

  function clearTimer() {
    if (timerId != null) {
      windowObj.clearTimeout(timerId);
      timerId = null;
    }
  }

  function clearWarningTickInterval() {
    if (warningTickIntervalId != null) {
      windowObj.clearInterval(warningTickIntervalId);
      warningTickIntervalId = null;
    }
  }

  function emitWarningChange({ force = false } = {}) {
    if (typeof onWarningChange !== "function") return;

    const remainingMs = Math.max(0, getRemainingMs());
    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const isVisible = !stopped && !timedOut && remainingMs <= normalizedWarningMs;

    const hasSecondChanged = remainingSeconds !== lastWarningSecond;
    const hasVisibleChanged = isVisible !== warningVisible;
    const shouldEmit = force || hasVisibleChanged || (isVisible && hasSecondChanged);
    if (!shouldEmit) {
      return;
    }

    warningVisible = isVisible;
    lastWarningSecond = remainingSeconds;
    onWarningChange({
      isVisible,
      remainingMs,
      remainingSeconds,
    });
  }

  function scheduleTimeout() {
    if (!hasBrowserObjects || stopped || timedOut) return;

    clearTimer();
    const remainingMs = getRemainingMs();

    if (remainingMs <= 0) {
      triggerTimeout();
      return;
    }

    timerId = windowObj.setTimeout(() => {
      triggerTimeout();
    }, remainingMs);

    emitWarningChange();
  }

  function publishActivity(at, { force = false } = {}) {
    if (!broadcastChannel) return;
    try {
      broadcastChannel.postMessage({ type: "activity", at, force: !!force });
    } catch {}
  }

  function publishTimeout(at) {
    if (!broadcastChannel) return;
    try {
      broadcastChannel.postMessage({ type: "timeout", at });
    } catch {}
  }

  function syncActivityAt(at, { persist = true, broadcast = true, force = false } = {}) {
    if (stopped || timedOut) return;
    if (!Number.isFinite(at)) return;
    if (requireWarningAcknowledgement && warningVisible && !force) {
      return;
    }

    if (at > lastActivityAt) {
      lastActivityAt = at;
      if (persist) {
        writeStoredNumber(storage, IDLE_ACTIVITY_STORAGE_KEY, at);
      }
      if (broadcast) {
        publishActivity(at, { force });
      }
    }

    scheduleTimeout();
  }

  function triggerTimeout() {
    if (stopped || timedOut) return;
    timedOut = true;
    clearTimer();
    clearWarningTickInterval();

    const timeoutAt = nowFn();
    writeStoredNumber(storage, IDLE_TIMEOUT_STORAGE_KEY, timeoutAt);
    publishTimeout(timeoutAt);

    try {
      onTimeout?.();
    } catch (error) {
      console.error("idle timeout callback failed:", error);
    }

    emitWarningChange({ force: true });
  }

  function reportActivity({ force = false } = {}) {
    syncActivityAt(nowFn(), { persist: true, broadcast: true, force });
  }

  function start() {
    if (!hasBrowserObjects || !stopped) return;

    stopped = false;
    timedOut = false;

    const now = nowFn();
    const storedActivity = readStoredNumber(storage, IDLE_ACTIVITY_STORAGE_KEY);
    lastActivityAt = Math.max(storedActivity ?? 0, now);
    writeStoredNumber(storage, IDLE_ACTIVITY_STORAGE_KEY, lastActivityAt);

    onActivity = () => {
      reportActivity();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      windowObj.addEventListener(eventName, onActivity, { passive: true });
    });

    onVisibility = () => {
      if (documentObj.visibilityState !== "visible") return;
      const stored = readStoredNumber(storage, IDLE_ACTIVITY_STORAGE_KEY);
      if (stored && stored > lastActivityAt) {
        lastActivityAt = stored;
      }
      scheduleTimeout();
    };
    documentObj.addEventListener("visibilitychange", onVisibility);

    onStorage = (event) => {
      if (event.key === IDLE_ACTIVITY_STORAGE_KEY) {
        const at = safeNumber(event.newValue);
        if (at && at > lastActivityAt) {
          lastActivityAt = at;
          scheduleTimeout();
        }
        return;
      }

      if (event.key === IDLE_TIMEOUT_STORAGE_KEY && event.newValue) {
        triggerTimeout();
      }
    };
    windowObj.addEventListener("storage", onStorage);

    broadcastChannel = createBroadcastChannel(IDLE_BROADCAST_CHANNEL);
    if (broadcastChannel) {
      onBroadcast = (event) => {
        const data = event?.data;
        if (data?.type === "activity") {
          const at = safeNumber(data.at);
          syncActivityAt(at, {
            persist: false,
            broadcast: false,
            force: data?.force === true,
          });
          return;
        }

        if (data?.type === "timeout") {
          triggerTimeout();
        }
      };

      broadcastChannel.addEventListener?.("message", onBroadcast);
      if (!broadcastChannel.addEventListener) {
        broadcastChannel.onmessage = onBroadcast;
      }
    }

    warningTickIntervalId = windowObj.setInterval(() => {
      if (stopped || timedOut) return;

      const remainingMs = getRemainingMs();
      if (remainingMs <= 0) {
        triggerTimeout();
        return;
      }

      emitWarningChange();
    }, 250);

    scheduleTimeout();
    emitWarningChange({ force: true });
  }

  function stop() {
    if (!hasBrowserObjects || stopped) return;
    stopped = true;

    clearTimer();
    clearWarningTickInterval();

    if (onActivity) {
      ACTIVITY_EVENTS.forEach((eventName) => {
        windowObj.removeEventListener(eventName, onActivity);
      });
    }

    if (onVisibility) {
      documentObj.removeEventListener("visibilitychange", onVisibility);
    }

    if (onStorage) {
      windowObj.removeEventListener("storage", onStorage);
    }

    if (broadcastChannel) {
      if (onBroadcast && broadcastChannel.removeEventListener) {
        broadcastChannel.removeEventListener("message", onBroadcast);
      } else {
        broadcastChannel.onmessage = null;
      }

      try {
        broadcastChannel.close?.();
      } catch {}
    }

    broadcastChannel = null;
    onStorage = null;
    onVisibility = null;
    onActivity = null;
    onBroadcast = null;
    warningVisible = false;
    lastWarningSecond = null;
    emitWarningChange({ force: true });
  }

  return {
    start,
    stop,
    reportActivity,
    getLastActivityAt: () => lastActivityAt,
  };
}
